import React, {useEffect, useState } from 'react';
import { ASSETS } from '../assets/assetMapping';
import { FilterState } from '../types/api';

// const imgSlider = ASSETS.ARROW_ICON; // Slider icon
const img = ASSETS.PLUS_ICON; // Plus icon
// const img1 = ASSETS.ARROW_ICON; // Arrow icon

interface SuggestionOption {
  id: string;
  prompt: string;
  filters: Partial<FilterState>;
}

interface SuggestionsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSuggestion: (prompt: string, filters: Partial<FilterState>) => void;
  isMobile?: boolean;
}

const suggestionOptions: SuggestionOption[] = [
  {
    id: '1',
    prompt: 'A professional woman in her 30s with straight black hair, wearing a navy blazer, friendly smile, soft daylight.',
    filters: {
      gender: 'female',
      age: '30s',
      hair: ['black'],
      background: 'any',
      style: {
        type: 'photorealistic',
        ratio: 'vertical',
        scene: 'headshot'
      }
    }
  },
  {
    id: '2',
    prompt: 'A young man with curly brown hair, casual hoodie, warm lighting, holding a laptop,expressive and upbeat',
    filters: {
      gender: 'male',
      age: '20s',
      hair: ['curly'],
      background: 'Holding laptop',
      style: {
        type: 'photorealistic',
        ratio: 'vertical',
        scene: 'mid-closeup'
      }
    }
  },
  {
    id: '3',
    prompt: 'Middle-aged man, glasses, short grey hair, wearing a button-down shirt, clear and approachable tone.',
    filters: {
      gender: 'male',
      age: '50s',
      hair: [],
      background: 'any',
      style: {
        type: 'photorealistic',
        ratio: 'vertical',
        scene: 'headshot'
      }
    }
  },
  {
    id: '4',
    prompt: 'A young woman with dyed pink hair, casual t-shirt, cinematic lighting, microphone in front of her.',
    filters: {
      gender: 'female',
      age: '20s',
      hair: [],
      background: 'Interview setup with mic',
      style: {
        type: 'photorealistic',
        ratio: 'vertical',
        scene: 'headshot'
      }
    }
  },
  {
    id: '5',
    prompt: 'An elderly woman with silver hair in a bun, wearing a cozy cardigan, sitting by a window with natural light, reading glasses, warm and wise expression.',
    filters: {
      gender: 'female',
      age: '60plus',
      hair: [],
      background: 'any',
      style: {
        type: 'photorealistic',
        ratio: 'vertical',
        scene: 'mid-closeup'
      }
    }
  },
  {
    id: '6',
    prompt: 'A teenager with messy blonde hair, wearing a graphic t-shirt, gaming headset around neck, colorful LED lighting, sitting at a gaming setup.',
    filters: {
      gender: 'any',
      age: 'under20',
      hair: ['blonde'],
      background: 'any',
      style: {
        type: 'photorealistic',
        ratio: 'vertical',
        scene: 'mid-closeup'
      }
    }
  },
  {
    id: '7',
    prompt: 'A fitness instructor in her 20s, athletic build, ponytail, wearing workout clothes, energetic and motivating pose.',
    filters: {
      gender: 'female',
      age: '20s',
      hair: [],
      background: 'any',
      style: {
        type: 'photorealistic',
        ratio: 'vertical',
        scene: 'mid-closeup'
      }
    }
  },
  {
    id: '8',
    prompt: 'A chef with a traditional white hat, apron, holding cooking utensils, confident and passionate expression.',
    filters: {
      gender: 'any',
      age: 'any',
      hair: [],
      background: 'any',
      style: {
        type: 'photorealistic',
        ratio: 'vertical',
        scene: 'mid-closeup'
      }
    }
  }
];

// Mobile Modal Components
function ExpandIndicator() {
  return (
    <div className="box-border content-stretch flex flex-col gap-2.5 items-center justify-center px-[174px] py-2.5 relative size-full">
      <div className="bg-[rgba(37,54,88,0.15)] h-1 rounded-[3px] shrink-0 w-[26px]" />
    </div>
  );
}

function HomeIndicator() {
  return (
    <div className="relative size-full">
      <div className="absolute bg-black bottom-2 h-[5px] rounded-[100px] translate-x-[-50%] w-[134px]" style={{ left: "calc(50% + 0.5px)" }} />
    </div>
  );
}

function Ai() {
  return (
    <div className="relative size-full">
      <div className="absolute left-px size-3.5 top-px">
        <img alt="" className="block max-w-none size-full" src={img} />
      </div>
    </div>
  );
}

const SuggestionsMenu: React.FC<SuggestionsMenuProps> = ({
  isOpen,
  onClose,
  onSelectSuggestion,
  isMobile = false
}) => {
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth < 1024); // lg breakpoint in Tailwind
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleSuggestionClick = (suggestion: SuggestionOption) => {
    onSelectSuggestion(suggestion.prompt, suggestion.filters);
    onClose();
  };

  const truncatePrompt = (prompt: string, maxLength: number = 50) => {
    return prompt.length > maxLength ? `${prompt.substring(0, maxLength)}...` : prompt;
  };

  if (!isOpen) return null;

  // Mobile Modal View
  if (isMobileView) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-end">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-[rgba(0,0,0,0.6)] backdrop-blur-[5px]"
          onClick={onClose}
        />
        
        {/* Modal Content */}
        <div className="relative bg-white w-full rounded-tl-[15px] rounded-tr-[15px] max-h-[80vh] flex flex-col">
          {/* Top Bar */}
          <div className="flex flex-col items-start justify-start">
            <div className="w-full flex items-center justify-center py-2.5">
              <ExpandIndicator />
            </div>
            <div className="w-full px-4 pb-4">
              <div className="flex items-center justify-center w-full">
                <h3 className="font-['Inter'] font-bold text-[18px] text-[#253658]">
                  Suggestions
                </h3>
              </div>
            </div>
          </div>

          {/* Suggestions List */}
          <div className="flex-1 overflow-y-auto px-4 pb-5">
            <div className="space-y-2">
              {suggestionOptions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full flex items-start gap-1.5 px-1 py-2 hover:bg-gray-50 transition-colors border-b border-[rgba(37,54,88,0.08)] last:border-b-0"
                >
                  <div className="opacity-80 overflow-clip relative shrink-0 size-6 mt-0.5">
                    <div className="absolute left-0.5 size-5 top-0.5">
                      <img alt="" className="block max-w-none size-full" src={img} />
                    </div>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-['Inter'] font-normal text-[14px] text-[#253658] leading-[1.4]">
                      {suggestion.prompt}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Home Indicator */}
          <div className="h-[34px] relative w-full">
            <HomeIndicator />
          </div>
        </div>
      </div>
    );
  }

  // Desktop View - Similar to GenerationHistory
  return (
    <div className="relative">
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">Suggestions</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title="Close suggestions"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Suggestions List */}
        <div className="max-h-80 overflow-y-auto">
          <div className="divide-y divide-gray-100">
            {suggestionOptions.map((suggestion) => (
              <div
                key={suggestion.id}
                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="flex items-start gap-3">
                  {/* AI Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center">
                      <div className="w-6 h-6 relative">
                        <Ai />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {truncatePrompt(suggestion.prompt, 60)}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            AI Suggestion
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center text-xs text-gray-500 flex-shrink-0">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        Use
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuggestionsMenu;
